"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import { Picker, Switch } from "@/features/home-v2/form/Form";
import { readPrice } from "../itemFilters";
import type { CollectionItems } from "../useCollectionItems";
type Currency = {
  id: number;
  symbol: string;
};
type Props = {
  s: CollectionItems;
  b: Dictionary["browse"];
  isFa: boolean;
  currencies: Currency[];
  /** Below 1024px the panel is a sheet over the page, with its own close and results buttons. */
  sheet: boolean;
  resultsLabel: string;
  onClose: () => void;
};

/** One trait and its values as ticks, with a search once the list is long. */
function TraitGroup(t0) {
  const $ = _c(31);
  const {
    trait,
    values,
    chosen,
    onToggle,
    b,
    num
  } = t0;
  const [open, setOpen] = useState(chosen.size > 0);
  const [find, setFind] = useState("");
  const id = useId();
  let t1;
  if ($[0] !== find || $[1] !== values) {
    const needle = find.trim().toLocaleLowerCase();
    t1 = needle ? values.filter(v => String(v.value).toLocaleLowerCase().includes(needle)) : values;
    $[0] = find;
    $[1] = values;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  const t2 = open || undefined;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(_temp);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== trait) {
    t4 = <span>{trait}</span>;
    $[4] = trait;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== chosen.size || $[7] !== num || $[8] !== values) {
    t5 = chosen.size ? <b>{num.format(chosen.size)}</b> : <small>{num.format(values.length)}</small>;
    $[6] = chosen.size;
    $[7] = num;
    $[8] = values;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronDown aria-hidden={true} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== id || $[12] !== open || $[13] !== t4 || $[14] !== t5) {
    t7 = <button type="button" className="vt-ft-trait-head v2-focus" aria-expanded={open} aria-controls={id} onClick={t3}>{t4}{t5}{t6}</button>;
    $[11] = id;
    $[12] = open;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== b || $[17] !== chosen || $[18] !== find || $[19] !== id || $[20] !== num || $[21] !== onToggle || $[22] !== open || $[23] !== shown || $[24] !== trait || $[25] !== values) {
    t8 = open ? <div id={id} className="vt-ft-trait-body">{values.length > 8 ? <label className="vt-ft-find"><Search aria-hidden={true} /><span className="v2-sr-only">{b.traitsSearch}</span><input type="search" value={find} onChange={e => setFind(e.target.value)} placeholder={b.traitsSearch} /></label> : null}<ul role="group" aria-label={trait}>{shown.map(v_1 => {
          const on = chosen.has(String(v_1.value));
          return <li key={String(v_1.value)}><button type="button" role="checkbox" aria-checked={on} className="vt-ft-value v2-focus" onClick={() => onToggle(String(v_1.value))}><span className="vt-ft-tick" aria-hidden={true}>{on ? <Check /> : null}</span><span className="vt-ft-value-name">{String(v_1.value)}</span><small>{num.format(v_1.count)}</small></button></li>;
        })}</ul></div> : null;
    $[16] = b;
    $[17] = chosen;
    $[18] = find;
    $[19] = id;
    $[20] = num;
    $[21] = onToggle;
    $[22] = open;
    $[23] = shown;
    $[24] = trait;
    $[25] = values;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t2 || $[28] !== t7 || $[29] !== t8) {
    t9 = <div className="vt-ft-trait" data-open={t2}>{t7}{t8}</div>;
    $[27] = t2;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}

/**
 * THE WORKS' FILTERS — sale status, offers, auctions, a price range in one
 * currency (or all), and the collection's own traits as ticks. Every choice
 * lands in the URL at once; the price waits for its «apply».
 */
function _temp(v_0) {
  return !v_0;
}
export default function ItemFilterPanel(t0) {
  const $ = _c(149);
  const {
    s,
    b,
    isFa,
    currencies,
    sheet,
    resultsLabel,
    onClose
  } = t0;
  const f = s.filters;
  const t1 = isFa ? "fa-IR" : "en-US";
  let t2;
  if ($[0] !== t1) {
    t2 = new Intl.NumberFormat(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const num = t2;
  const ids = useId();
  const [min, setMin] = useState(f.priceMin ?? "");
  const [max, setMax] = useState(f.priceMax ?? "");
  let t3;
  let t4;
  if ($[2] !== f.priceMax || $[3] !== f.priceMin) {
    t3 = () => {
      setMin(f.priceMin ?? "");
      setMax(f.priceMax ?? "");
    };
    t4 = [f.priceMin, f.priceMax];
    $[2] = f.priceMax;
    $[3] = f.priceMin;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== min) {
    t5 = !min.trim() || readPrice(min) != null;
    $[6] = min;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const minOk = t5;
  let t6;
  if ($[8] !== max) {
    t6 = !max.trim() || readPrice(max) != null;
    $[8] = max;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const maxOk = t6;
  let t7;
  if ($[10] !== max || $[11] !== maxOk || $[12] !== min || $[13] !== minOk || $[14] !== s) {
    t7 = e => {
      e.preventDefault();
      if (!minOk || !maxOk) {
        return;
      }
      let lo = readPrice(min);
      let hi = readPrice(max);
      if (lo && hi && Number(lo) > Number(hi)) {
        [lo, hi] = [hi, lo];
      }
      s.setFilters({
        priceMin: lo,
        priceMax: hi
      });
    };
    $[10] = max;
    $[11] = maxOk;
    $[12] = min;
    $[13] = minOk;
    $[14] = s;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const applyPrice = t7;
  let map;
  if ($[16] !== f.traits) {
    map = new Map();
    f.traits.forEach(p => {
      if (!map.has(p.trait)) {
        map.set(p.trait, new Set());
      }
      map.get(p.trait).add(p.value);
    });
    $[16] = f.traits;
    $[17] = map;
  } else {
    map = $[17];
  }
  const chosenByTrait = map;
  let t8;
  if ($[18] !== b.statusAll) {
    t8 = {
      key: null,
      label: b.statusAll
    };
    $[18] = b.statusAll;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== b.statusListed) {
    t9 = {
      key: "1",
      label: b.statusListed
    };
    $[20] = b.statusListed;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== b.statusUnlisted) {
    t10 = {
      key: "0",
      label: b.statusUnlisted
    };
    $[22] = b.statusUnlisted;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = [t8, t9, t10];
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const status = t11 as const;
  let t12;
  let t13;
  if ($[28] !== onClose || $[29] !== sheet) {
    t12 = () => {
      if (!sheet) {
        return;
      }
      const esc = e_0 => {
        if (e_0.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", esc);
      return () => document.removeEventListener("keydown", esc);
    };
    t13 = [sheet, onClose];
    $[28] = onClose;
    $[29] = sheet;
    $[30] = t12;
    $[31] = t13;
  } else {
    t12 = $[30];
    t13 = $[31];
  }
  useEffect(t12, t13);
  const t14 = b.filters;
  const t15 = sheet || undefined;
  let t16;
  if ($[32] !== b.filters || $[33] !== b.hideFilters || $[34] !== onClose || $[35] !== sheet) {
    t16 = sheet ? <div className="vt-ft-top"><b>{b.filters}</b><button type="button" className="vt-ft-close v2-focus" onClick={onClose} aria-label={b.hideFilters}><X aria-hidden={true} /></button></div> : null;
    $[32] = b.filters;
    $[33] = b.hideFilters;
    $[34] = onClose;
    $[35] = sheet;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = `${ids}-status`;
  const t18 = `${ids}-status`;
  let t19;
  if ($[37] !== b.statusLabel || $[38] !== t18) {
    t19 = <h3 id={t18} className="vt-ft-h">{b.statusLabel}</h3>;
    $[37] = b.statusLabel;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const t20 = `${ids}-status`;
  let t21;
  if ($[40] !== f.listed || $[41] !== s || $[42] !== status) {
    t21 = status.map(o => <button key={o.label} type="button" className="v2-focus" aria-pressed={f.listed === o.key} onClick={() => s.setFilters({
      listed: o.key
    })}>{o.label}</button>);
    $[40] = f.listed;
    $[41] = s;
    $[42] = status;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t20 || $[45] !== t21) {
    t22 = <div className="vt-ft-seg" role="group" aria-labelledby={t20}>{t21}</div>;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== b.hasOffers) {
    t23 = <span>{b.hasOffers}</span>;
    $[47] = b.hasOffers;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== s) {
    t24 = v => s.setFilters({
      hasOffers: v
    });
    $[49] = s;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== b.hasOffers || $[52] !== f.hasOffers || $[53] !== t24) {
    t25 = <Switch checked={f.hasOffers} onChange={t24} label={b.hasOffers} />;
    $[51] = b.hasOffers;
    $[52] = f.hasOffers;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t23 || $[56] !== t25) {
    t26 = <div className="vt-ft-row">{t23}{t25}</div>;
    $[55] = t23;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== b.onAuction) {
    t27 = <span>{b.onAuction}</span>;
    $[58] = b.onAuction;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== s) {
    t28 = v_0 => s.setFilters({
      onAuction: v_0
    });
    $[60] = s;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== b.onAuction || $[63] !== f.onAuction || $[64] !== t28) {
    t29 = <Switch checked={f.onAuction} onChange={t28} label={b.onAuction} />;
    $[62] = b.onAuction;
    $[63] = f.onAuction;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t27 || $[67] !== t29) {
    t30 = <div className="vt-ft-row">{t27}{t29}</div>;
    $[66] = t27;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t17 || $[70] !== t19 || $[71] !== t22 || $[72] !== t26 || $[73] !== t30) {
    t31 = <section className="vt-ft-sec" aria-labelledby={t17}>{t19}{t22}{t26}{t30}</section>;
    $[69] = t17;
    $[70] = t19;
    $[71] = t22;
    $[72] = t26;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  const t32 = `${ids}-price`;
  const t33 = `${ids}-price`;
  let t34;
  if ($[75] !== b.priceLabel || $[76] !== t33) {
    t34 = <h3 id={t33} className="vt-ft-h">{b.priceLabel}</h3>;
    $[75] = b.priceLabel;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== b.allCurrencies || $[79] !== b.currencyLabel || $[80] !== currencies || $[81] !== f.currencyId || $[82] !== s) {
    t35 = currencies.length ? <Picker block={true} value={f.currencyId ? String(f.currencyId) : ""} label={b.currencyLabel} options={[{
      value: "",
      label: b.allCurrencies
    }, ...currencies.map(_temp2)]} onChange={v_1 => s.setFilters({
      currencyId: v_1 ? Number(v_1) : null
    })} /> : null;
    $[78] = b.allCurrencies;
    $[79] = b.currencyLabel;
    $[80] = currencies;
    $[81] = f.currencyId;
    $[82] = s;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  const t36 = !minOk || undefined;
  let t37;
  if ($[84] !== b.priceMin) {
    t37 = <span className="v2-sr-only">{b.priceMin}</span>;
    $[84] = b.priceMin;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = e_1 => setMin(e_1.target.value);
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  const t39 = !minOk || undefined;
  let t40;
  if ($[87] !== b.priceMin || $[88] !== min || $[89] !== t39) {
    t40 = <input dir="ltr" inputMode="decimal" autoComplete="off" value={min} onChange={t38} placeholder={b.priceMin} aria-invalid={t39} />;
    $[87] = b.priceMin;
    $[88] = min;
    $[89] = t39;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t36 || $[92] !== t37 || $[93] !== t40) {
    t41 = <label className="vt-ft-num" data-invalid={t36}>{t37}{t40}</label>;
    $[91] = t36;
    $[92] = t37;
    $[93] = t40;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="vt-ft-dash" aria-hidden={true}>–</span>;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  const t43 = !maxOk || undefined;
  let t44;
  if ($[96] !== b.priceMax) {
    t44 = <span className="v2-sr-only">{b.priceMax}</span>;
    $[96] = b.priceMax;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = e_2 => setMax(e_2.target.value);
    $[98] = t45;
  } else {
    t45 = $[98];
  }
  const t46 = !maxOk || undefined;
  let t47;
  if ($[99] !== b.priceMax || $[100] !== max || $[101] !== t46) {
    t47 = <input dir="ltr" inputMode="decimal" autoComplete="off" value={max} onChange={t45} placeholder={b.priceMax} aria-invalid={t46} />;
    $[99] = b.priceMax;
    $[100] = max;
    $[101] = t46;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t43 || $[104] !== t44 || $[105] !== t47) {
    t48 = <label className="vt-ft-num" data-invalid={t43}>{t44}{t47}</label>;
    $[103] = t43;
    $[104] = t44;
    $[105] = t47;
    $[106] = t48;
  } else {
    t48 = $[106];
  }
  const t49 = !minOk || !maxOk;
  let t50;
  if ($[107] !== b.priceApply || $[108] !== t49) {
    t50 = <button type="submit" className="v2-btn v2-btn--ghost v2-focus" disabled={t49}>{b.priceApply}</button>;
    $[107] = b.priceApply;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== applyPrice || $[111] !== t41 || $[112] !== t48 || $[113] !== t50) {
    t51 = <form className="vt-ft-price" onSubmit={applyPrice} noValidate={true}>{t41}{t42}{t48}{t50}</form>;
    $[110] = applyPrice;
    $[111] = t41;
    $[112] = t48;
    $[113] = t50;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== t32 || $[116] !== t34 || $[117] !== t35 || $[118] !== t51) {
    t52 = <section className="vt-ft-sec" aria-labelledby={t32}>{t34}{t35}{t51}</section>;
    $[115] = t32;
    $[116] = t34;
    $[117] = t35;
    $[118] = t51;
    $[119] = t52;
  } else {
    t52 = $[119];
  }
  const t53 = `${ids}-traits`;
  const t54 = `${ids}-traits`;
  let t55;
  if ($[120] !== b.traitsLabel || $[121] !== t54) {
    t55 = <h3 id={t54} className="vt-ft-h">{b.traitsLabel}</h3>;
    $[120] = b.traitsLabel;
    $[121] = t54;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  let t56;
  if ($[123] !== b || $[124] !== chosenByTrait || $[125] !== num || $[126] !== s) {
    t56 = s.traitsLoading ? <div className="vt-ft-skel" aria-hidden={true}><span className="v2-cd-shimmer" /><span className="v2-cd-shimmer" /><span className="v2-cd-shimmer" /></div> : s.catalogue.length === 0 ? <p className="vt-ft-note">{b.traitsEmpty}</p> : <div className="vt-ft-traits">{s.catalogue.map(g => <TraitGroup key={g.trait} trait={g.trait} values={g.values} chosen={chosenByTrait.get(g.trait) ?? new Set()} b={b} num={num} onToggle={value => s.toggleTrait({
        trait: g.trait,
        value
      })} />)}</div>;
    $[123] = b;
    $[124] = chosenByTrait;
    $[125] = num;
    $[126] = s;
    $[127] = t56;
  } else {
    t56 = $[127];
  }
  let t57;
  if ($[128] !== t53 || $[129] !== t55 || $[130] !== t56) {
    t57 = <section className="vt-ft-sec" aria-labelledby={t53}>{t55}{t56}</section>;
    $[128] = t53;
    $[129] = t55;
    $[130] = t56;
    $[131] = t57;
  } else {
    t57 = $[131];
  }
  let t58;
  if ($[132] !== t31 || $[133] !== t52 || $[134] !== t57) {
    t58 = <div className="vt-ft-scroll">{t31}{t52}{t57}</div>;
    $[132] = t31;
    $[133] = t52;
    $[134] = t57;
    $[135] = t58;
  } else {
    t58 = $[135];
  }
  let t59;
  if ($[136] !== b.clearAll || $[137] !== onClose || $[138] !== resultsLabel || $[139] !== s.clearFilters || $[140] !== s.filterCount || $[141] !== sheet) {
    t59 = sheet ? <div className="vt-ft-foot">{s.filterCount ? <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={s.clearFilters}>{b.clearAll}</button> : null}<button type="button" className="v2-btn v2-focus" onClick={onClose}>{resultsLabel}</button></div> : null;
    $[136] = b.clearAll;
    $[137] = onClose;
    $[138] = resultsLabel;
    $[139] = s.clearFilters;
    $[140] = s.filterCount;
    $[141] = sheet;
    $[142] = t59;
  } else {
    t59 = $[142];
  }
  let t60;
  if ($[143] !== b.filters || $[144] !== t15 || $[145] !== t16 || $[146] !== t58 || $[147] !== t59) {
    t60 = <aside className="vt-ft" aria-label={t14} data-sheet={t15}>{t16}{t58}{t59}</aside>;
    $[143] = b.filters;
    $[144] = t15;
    $[145] = t16;
    $[146] = t58;
    $[147] = t59;
    $[148] = t60;
  } else {
    t60 = $[148];
  }
  return t60;
}
function _temp2(c) {
  return {
    value: String(c.id),
    label: c.symbol
  };
}